import React, { useState } from 'react';
import {
    View,
    Text,
    ScrollView,
    TouchableOpacity,
    StyleSheet,
    TextInput,
    ActivityIndicator,
    Alert,
    Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { LinearGradient } from 'expo-linear-gradient';
import { bookService } from '../../api/marketplace';
import { H2, H3, Caption, Body1, Body2 } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';

const ServiceCheckoutScreen = ({ route, navigation }) => {
    const { service } = route.params;
    const insets = useSafeAreaInsets();
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [notes, setNotes] = useState('');

    const dates = [
        { day: 'Mon', date: '21', month: 'Feb' },
        { day: 'Tue', date: '22', month: 'Feb' },
        { day: 'Wed', date: '23', month: 'Feb' },
        { day: 'Thu', date: '24', month: 'Feb' },
        { day: 'Fri', date: '25', month: 'Feb' },
    ];

    const times = ['09:00 AM', '11:00 AM', '02:00 PM', '04:00 PM', '06:00 PM'];

    const handleConfirmBooking = async () => {
        if (!selectedDate || !selectedTime) {
            Alert.alert("Selection Required", "Please select a date and time for your service.");
            return;
        }

        try {
            setLoading(true);
            const scheduled_at = new Date(`2026-02-${selectedDate.date} ${selectedTime}`);

            const bookingData = {
                service_id: service.id,
                provider_id: service.listing_id,
                scheduled_at,
                notes,
                total_price: service.price
            };

            await bookService(bookingData);

            Alert.alert(
                "Booking Confirmed",
                "Your request has been sent to the provider. You'll be notified once they confirm.",
                [{ text: "Great!", onPress: () => navigation.navigate('OrderHistory') }]
            );
        } catch (error) {
            Alert.alert("Booking Failed", error || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <View style={[styles.container, { paddingTop: insets.top }]}>
            <StatusBar style="dark" />

            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                    <Ionicons name="arrow-back" size={24} color="#111827" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Book Service</Text>
                <View style={{ width: 40 }} />
            </View>

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
                {/* Service Summary */}
                <View style={styles.summaryCard}>
                    <View style={styles.summaryTop}>
                        <Ionicons name="construct" size={24} color="#6366F1" />
                        <View style={styles.summaryInfo}>
                            <Text style={styles.serviceName}>{service.name}</Text>
                            <Text style={styles.servicePrice}>KES {Number(service.price).toLocaleString()} / {service.unit || 'project'}</Text>
                        </View>
                    </View>
                </View>

                {/* Date Selection */}
                <Text style={styles.sectionLabel}>Select Date</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.datesContainer}>
                    {dates.map((d, i) => (
                        <TouchableOpacity
                            key={i}
                            style={[
                                styles.dateCard,
                                selectedDate?.date === d.date && styles.dateCardSelected
                            ]}
                            onPress={() => setSelectedDate(d)}
                        >
                            <Text style={[styles.dateDay, selectedDate?.date === d.date && styles.dateTextSelected]}>{d.day}</Text>
                            <Text style={[styles.dateDate, selectedDate?.date === d.date && styles.dateTextSelected]}>{d.date}</Text>
                            <Text style={[styles.dateMonth, selectedDate?.date === d.date && styles.dateTextSelected]}>{d.month}</Text>
                        </TouchableOpacity>
                    ))}
                </ScrollView>

                {/* Time Selection */}
                <Text style={styles.sectionLabel}>Select Prefered Time Slot</Text>
                <View style={styles.timesGrid}>
                    {times.map((t, i) => (
                        <TouchableOpacity
                            key={i}
                            style={[
                                styles.timePill,
                                selectedTime === t && styles.timePillSelected
                            ]}
                            onPress={() => setSelectedTime(t)}
                        >
                            <Text style={[styles.timeText, selectedTime === t && styles.timeTextSelected]}>{t}</Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {/* Notes */}
                <Text style={styles.sectionLabel}>Additional Notes (Optional)</Text>
                <TextInput
                    style={styles.notesInput}
                    placeholder="E.g. Specific requirements, exact location details, etc."
                    multiline
                    numberOfLines={4}
                    value={notes}
                    onChangeText={setNotes}
                    placeholderTextColor="#94A3B8"
                />

                {/* Price Breakdown */}
                <View style={styles.breakdownCard}>
                    <View style={styles.breakdownRow}>
                        <Text style={styles.breakdownLabel}>Service Fee</Text>
                        <Text style={styles.breakdownValue}>KES {Number(service.price).toLocaleString()}</Text>
                    </View>
                    <View style={styles.breakdownRow}>
                        <Text style={styles.breakdownLabel}>Estimated Duration</Text>
                        <Text style={styles.breakdownValue}>{service.service_config?.duration_estimate || '2 hours'}</Text>
                    </View>
                    <View style={[styles.breakdownRow, styles.totalRow]}>
                        <Text style={styles.totalLabel}>Total Payable</Text>
                        <Text style={styles.totalValue}>KES {Number(service.price).toLocaleString()}</Text>
                    </View>
                </View>

                <View style={styles.infoBox}>
                    <Ionicons name="shield-checkmark" size={16} color="#6366F1" />
                    <Text style={styles.infoText}>Funds will be held in escrow and only released after service completion.</Text>
                </View>
            </ScrollView>

            <View style={styles.footer}>
                <TouchableOpacity
                    style={[styles.confirmBtn, loading && styles.btnDisabled]}
                    onPress={handleConfirmBooking}
                    disabled={loading}
                >
                    {loading ? (
                        <ActivityIndicator color="#FFF" />
                    ) : (
                        <Text style={styles.confirmBtnText}>Confirm Booking Request</Text>
                    )}
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 15,
        backgroundColor: '#FFF',
    },
    backBtn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
    },
    scrollContent: {
        padding: 20,
    },
    summaryCard: {
        backgroundColor: '#FFF',
        borderRadius: 20,
        padding: 16,
        marginBottom: 25,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        ...getShadow('xs'),
    },
    summaryTop: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    summaryInfo: {
        flex: 1,
    },
    serviceName: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    servicePrice: {
        fontSize: 14,
        color: '#6366F1',
        fontWeight: '700',
        marginTop: 2,
    },
    sectionLabel: {
        fontSize: 14,
        fontWeight: '800',
        color: '#475569',
        marginBottom: 12,
    },
    datesContainer: {
        marginBottom: 25,
    },
    dateCard: {
        width: 70,
        height: 90,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
    },
    dateCardSelected: {
        backgroundColor: '#6366F1',
        borderColor: '#6366F1',
    },
    dateDay: {
        fontSize: 12,
        color: '#64748B',
        fontWeight: '700',
    },
    dateDate: {
        fontSize: 20,
        fontWeight: '900',
        color: '#111827',
        marginVertical: 4,
    },
    dateMonth: {
        fontSize: 10,
        color: '#94A3B8',
        fontWeight: '800',
    },
    dateTextSelected: {
        color: '#FFF',
    },
    timesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 25,
    },
    timePill: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
    },
    timePillSelected: {
        backgroundColor: '#6366F1',
        borderColor: '#6366F1',
    },
    timeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#475569',
    },
    timeTextSelected: {
        color: '#FFF',
    },
    notesInput: {
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 16,
        padding: 15,
        height: 100,
        textAlignVertical: 'top',
        fontSize: 14,
        color: '#111827',
        marginBottom: 25,
    },
    breakdownCard: {
        backgroundColor: '#F8FAFB',
        borderRadius: 16,
        padding: 16,
        gap: 12,
        marginBottom: 20,
    },
    breakdownRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    breakdownLabel: {
        fontSize: 13,
        color: '#64748B',
        fontWeight: '600',
    },
    breakdownValue: {
        fontSize: 13,
        color: '#1F2937',
        fontWeight: '700',
    },
    totalRow: {
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
        paddingTop: 12,
        marginTop: 4,
    },
    totalLabel: {
        fontSize: 15,
        fontWeight: '800',
        color: '#111827',
    },
    totalValue: {
        fontSize: 18,
        fontWeight: '900',
        color: '#6366F1',
    },
    infoBox: {
        flexDirection: 'row',
        backgroundColor: '#F5F7FF',
        padding: 12,
        borderRadius: 12,
        gap: 8,
        alignItems: 'center',
    },
    infoText: {
        fontSize: 11,
        color: '#6366F1',
        fontWeight: '600',
        flex: 1,
    },
    footer: {
        padding: 20,
        paddingBottom: Platform.OS === 'ios' ? 35 : 20,
        backgroundColor: '#FFF',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    confirmBtn: {
        height: 52,
        backgroundColor: '#6366F1',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        ...getShadow('md'),
    },
    btnDisabled: {
        opacity: 0.6,
    },
    confirmBtnText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '800',
    },
});

export default ServiceCheckoutScreen;
